import * as Yup from 'yup';
import moment from 'moment';

const validateEmailDomain = (value: string | undefined): boolean => {
  if (!value) return true; // Skip validation if the value is empty
  return value.toLowerCase().endsWith('.com');
};

// Custom validation for numeric input
interface NumericValidationFunction {
  (value: string): boolean;
}

const isNumeric: NumericValidationFunction = (value) => {
  return /^\d*\.?\d*$/.test(value);
};


export const getValidationSchema = (step: number, state: string) => {
  switch (step) {
    case 0:
      if (state === 'PTRA') {

        return Yup.object().shape({
          programInfo: Yup.string().required('Program Info is required'),
          purchaseOrderNumber: Yup.string()
            .when(['programInfo', 'arlingtonOrGrandPrairieLocationFlag'], {
              is: (programInfo: string, arlingtonOrGrandPrairieLocationFlag: string) => ((programInfo === 'Pepsi' && arlingtonOrGrandPrairieLocationFlag === 'No') || programInfo === 'Frito-Lay' || programInfo === 'DFA'),
              then: (schema: Yup.StringSchema) => schema
                .required('Purchase Order Number is required')
                .nullable(),
            })
            .when(['programInfo', 'arlingtonOrGrandPrairieLocationFlag'], {
              is: (programInfo: string, arlingtonOrGrandPrairieLocationFlag: string) =>
                programInfo === 'Pepsi' && arlingtonOrGrandPrairieLocationFlag === 'Yes',
              then: (schema) => schema.test(
                'either-costcenter-or-PO-number-required',
                'Either PO number or Cost Center is required',
                function (value) {
                  const { costCenter } = this.parent;
                  return !!value || !!costCenter;
                }
              ),
            }),
          pickupNumber: Yup.string()
            .matches(/^[a-zA-Z0-9]*$/, 'Pickup Number must be alphanumeric'),
          costCenter: Yup.string()
            .matches(/^[\d\W]+$/, 'Cost Center must be numeric')
            .when(['programInfo', 'researchAndDevelopmentFlag', 'arlingtonOrGrandPrairieLocationFlag'], {
              is: (programInfo: string, researchAndDevelopmentFlag: string, arlingtonOrGrandPrairieLocationFlag: string) =>
                (programInfo === 'Pepsi' && researchAndDevelopmentFlag === 'Yes') || arlingtonOrGrandPrairieLocationFlag === 'No',
              then: (schema: Yup.StringSchema) => schema.required('Cost Center is required'),
            })
            .when('programInfo', {
              is: (programInfo: string) => programInfo === 'Gatorade/Quaker',
              then: (schema: Yup.StringSchema) => schema.required('Cost Center is required'),
            })
            .test(
              'is-cost-center-length-valid-frito-lay',
              'Cost center must be 10 digits',
              function (value) {
                const { programInfo, researchAndDevelopmentFlag } = this.parent;
                if (programInfo === 'Frito-Lay' && researchAndDevelopmentFlag === 'Yes' && value) {
                  return /^\d{10}$/.test(value);
                }
                return true;
              }
            ),
          researchAndDevelopmentFlag: Yup.string().test(
            'is-required-for-frito-lay-or-Pepsi',
            'Research and Development Flag is required',
            function (value) {
              const { programInfo } = this.parent;
              if (programInfo === 'Frito-Lay') {
                return !!value;
              }
              return true;
            }
          ),
          accountProjectNumber: Yup.string()
            .matches(/^[a-zA-Z0-9]*$/, 'Account Project Number must be alphanumeric'),
          divLoc: Yup.string()
            .matches(/^\d+$/, 'DivLoc must be numeric')
            .test(
              'is-required-and-numeric-for-frito-lay',
              'DivLoc is required for Frito-Lay',
              function (value) {
                const { programInfo, researchAndDevelopmentFlag } = this.parent;
                if (programInfo === 'Frito-Lay' && (researchAndDevelopmentFlag === 'Yes' || researchAndDevelopmentFlag === 'No')) {
                  return !!value && /^\d+$/.test(value);
                }
                return true;
              }
            ),
          pepLoc: Yup.string()
            .test(
              'pepLoc-numeric',
              'PepLoc must be numeric',
              function (value) {
                const { programInfo, arlingtonOrGrandPrairieLocationFlag } = this.parent;
                if (programInfo === 'Pepsi' && arlingtonOrGrandPrairieLocationFlag === 'No' && value) {
                  return !!value && /^[A-Za-z0-9\s]+$/.test(value);
                }
                return true;
              }
            )
            .when(['programInfo', 'researchAndDevelopmentFlag', 'arlingtonOrGrandPrairieLocationFlag'], {
              is: (programInfo: string, researchAndDevelopmentFlag: string, arlingtonOrGrandPrairieLocationFlag: string) =>
                (programInfo === 'Pepsi' && researchAndDevelopmentFlag === 'Yes') || (arlingtonOrGrandPrairieLocationFlag === 'No'),
              then: (schema) => schema.test(
                'either-pepLoc-or-accountProjectNumber',
                'Either PepLoc or Account/Project Number is required',
                function (value) {
                  const { accountProjectNumber } = this.parent;
                  return !!value || !!accountProjectNumber;
                }
              ),
            }),
          arlingtonOrGrandPrairieLocationFlag: Yup.string().when(['programInfo', 'researchAndDevelopmentFlag'], {
            is: (programInfo: string, researchAndDevelopmentFlag: string) => programInfo === 'Pepsi' && researchAndDevelopmentFlag === 'No',
            then: (schema) => schema.required('Arlington or Grand Prairie Location Flag is required '),
          }),
        }).test('purchaseOrderNumber-or-costCenter', 'Either Purchase Order Number or Cost Center is required', function (value) {
          const { programInfo, purchaseOrderNumber, costCenter } = value;
          if (programInfo === 'DFA') {
            return !!purchaseOrderNumber || !!costCenter;
          }
          return true;
        });

      }
      else {
        return Yup.object().shape({
          purchaseOrderNumber: Yup.string()
            .required('Purchase Order is required')
            .test(
              'no-duplicates',
              'Duplicate purchase order numbers are not allowed',
              function (value) {
                if (!value) return true; // If the field is empty, no need to check for duplicates
                const purchaseOrderNumbers = value.split(';').map(po => po.trim());
                const uniquePOs = new Set(purchaseOrderNumbers);
                return uniquePOs.size === purchaseOrderNumbers.length;
              }
            )
            .test(
              'max-count',
              'The list of PO numbers should not exceed 20.',
              function (value) {
                if (!value) return true; // If the field is empty, no need to check for count
                const purchaseOrderNumbers = value.split(';').map(po => po.trim());
                return purchaseOrderNumbers.length <= 20;
              }
            ),
          pickupNumber: Yup.string()
            .required('Pick Up Number is required'),

        });
      }
    case 1:

      return Yup.object().shape({
        requesterName: Yup.string()
          .matches(/^[A-Za-z\s]+$/, 'Requesters Name must be alphabetical')
          .required('Requesters Name is required'),
        /* requesterEmail: Yup.string()
          .email('Invalid email address')
          .test('domain', 'Email must end with .com', validateEmailDomain)
          .nullable() // Allow null for the conditional check
          .test('email-or-phone', 'Either requesterEmail or requesterPhoneNumber is required', function (value) {
            const { requesterPhoneNumber } = this.parent;
            return !!value || !!requesterPhoneNumber;
          }), */
        requesterPhoneNumber: Yup.string()
          .matches(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, 'Phone number must be in a valid format')
          .test(
            'is-valid-us-phone',
            'Phone number must be a valid US phone number',
            (value) => {
              if (!value) return true; // Allow empty for the conditional check
              const cleaned = ('' + value).replace(/\D/g, '');
              return cleaned.length === 10; // Validates if the phone number has exactly 10 digits
            }
          )
          .nullable(), // Allow null for the conditional check

        alternateName: Yup.string()
          .matches(/^[A-Za-z\s]+$/, 'Alternate Name must be alphabetical')
          .test(
            'name-required',
            'Alternate Name is required when either Alternate Email or Alternate Phone Number is entered',
            function (value) {
              const { alternateEmail, alternatePhoneNumber } = this.parent;
              if (alternateEmail || alternatePhoneNumber) {
                return !!value; // Ensure value is not empty
              }
              return true; // If neither is provided, validation passes
            }
          ),
        alternateEmail: Yup.string()
          .email('Invalid email address')
          .test(
            'either-email-or-phone',
            'Either Alternate Email or Alternate Phone Number is required when Alternate Name is entered',
            function (value) {
              const { alternateName, alternatePhoneNumber } = this.parent;
              if (alternateName) {
                return !!value || !!alternatePhoneNumber; // At least one must be filled
              }
              return true; // If Alternate Name is not entered, validation passes
            }
          ),
        alternatePhoneNumber: Yup.string()
          .matches(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, 'Phone number must be in a valid format')
          .test(
            'is-valid-us-phone',
            'Phone number must be a valid US phone number',
            (value) => {
              if (!value) return true; // Allow empty for the conditional check
              const cleaned = ('' + value).replace(/\D/g, '');
              return cleaned.length === 10; // Validates if the phone number has exactly 10 digits
            }
          )
          .nullable()
          .test(
            'either-email-or-phone',
            'Either Alternate Email or Alternate Phone Number is required when Alternate Name is entered',
            function (value) {
              const { alternateName, alternateEmail } = this.parent;
              if (alternateName) {
                return !!value || !!alternateEmail; // At least one must be filled
              }
              return true; // If Alternate Name is not entered, validation passes
            }
          ),

      });

    case 2:
      return Yup.object().shape({
        shippingCompanyName: Yup.string()
          .required('Company Name is required'),
        shippingStreetAddress: Yup.string()
          .required('Street Address is required'),
        shippingCity: Yup.string()
          .required('City is required'),
        shippingState: Yup.string()
          .required('State is required'),
        shippingZipCode: Yup.string()
          .required('Zip Code is required'),
        shippingDockDoor: Yup.number()
          .typeError('Dock Door must be numeric'),
        shippingDockHeightFlag: Yup.string()
          .required('Dock Height Required is required'),
        shippingContactName: Yup.string().required('Contact Name is required').matches(/^[A-Za-z\s]+$/, 'Contact Name must be alphabetical'),
        shippingContactEmail: Yup.string().email('Invalid email').test('domain', 'Email must end with .com', validateEmailDomain),
        shippingContactPhoneNumber: Yup.string()
          .matches(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, 'Phone number must be in a valid format')
          .test(
            'is-valid-us-phone',
            'Phone number must be a valid US phone number',
            (value) => {
              if (!value) return true; // Allow empty for the conditional check
              const cleaned = ('' + value).replace(/\D/g, '');
              return cleaned.length === 10; // Validates if the phone number has exactly 10 digits
            }
          )
          .nullable(), // Allow null for the conditional check


        additionalContactsList: Yup.array().of(
          Yup.object().shape({
            alternateShippingContactName: Yup.string()
              .matches(/^[A-Za-z\s]+$/, 'Contact name must be alphabetical'),
            alternateShippingContactEmail: Yup.string()
              .email('Invalid email address')
              .test('domain', 'Email must end with .com', validateEmailDomain),
            alternateShippingContactPhoneNumber: Yup.string()
              .matches(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, 'Phone number must be in a valid format')
              .test(
                'is-valid-us-phone',
                'Phone number must be a valid US phone number',
                (value) => {
                  if (!value) return true; // Allow empty for the conditional check
                  const cleaned = ('' + value).replace(/\D/g, '');
                  return cleaned.length === 10; // Validates if the phone number has exactly 10 digits
                }
              )
              .nullable(), // Allow null for the conditional check
          }).test(
            'name-required-when-email-or-phone',
            'Contact name is required when email or phone number is provided',
            function (value) {
              const { alternateShippingContactName, alternateShippingContactEmail, alternateShippingContactPhoneNumber } = value;
              if ((alternateShippingContactEmail || alternateShippingContactPhoneNumber) && !alternateShippingContactName) {
                return this.createError({
                  path: `${this.path}.alternateShippingContactName`,
                  message: 'Contact name is required when email or phone number is provided',
                });
              }
              return true;
            }
          ).test(
            'email-or-phone-required',
            'Either email or phone number is required when contact name is provided',
            function (value) {
              const { alternateShippingContactName, alternateShippingContactEmail, alternateShippingContactPhoneNumber } = value;
              if (alternateShippingContactName && !alternateShippingContactEmail && !alternateShippingContactPhoneNumber) {
                return this.createError({
                  path: `${this.path}.alternateShippingContactEmail`,
                  message: 'Either email or phone number is required when contact name is provided',
                });
              }
              return true;
            }
          )
        ),

        /*  alternateShippingContactName: Yup.string()
         .matches(/^[A-Za-z]+$/, 'Alternate Shipping Contact Name should contain only alphabetic characters'),
         
       alternateShippingContactEmail: Yup.string()
         .email('Invalid email address')
         .test('domain', 'Email must end with .com', validateEmailDomain)
         .when('alternateShippingContactName', {
           is: (alternateShippingContactName: string) => !!alternateShippingContactName,
           then: (schema) => schema.required('Alternate Shipping Contact Email is required when Alternate Name is entered'),
         }),
     
       alternateShippingContactPhoneNumber:  Yup.string()
       .matches(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, 'Phone number must be in a valid format')
       .test(
         'is-valid-us-phone',
         'Phone number must be a valid US phone number',
         (value) => {
           if (!value) return true; // Allow empty for the conditional check
           const cleaned = ('' + value).replace(/\D/g, '');
           return cleaned.length === 10; // Validates if the phone number has exactly 10 digits
         }
       )
       .nullable(), // Allow null for the conditional check */
        shippingProductReadyDate: Yup.string()
          .required('Product Ready Date is required')
          .test('is-not-past-dated', 'Product Ready Date cannot be in the past', function (value) {
            return new Date(value).getTime() >= new Date(new Date().setHours(0, 0, 0, 0)).getTime();
          }),
        shippingProductReadyTime: Yup.string()
          .required('Product Ready Time is required')
          .test('before-close-time', 'Product Ready Time should be before Shipper Close Time', function (value) {
            const { shipperCloseTime } = this.parent;

            if (!value || !shipperCloseTime) return true;

            const receiveTime = moment(value, 'HH:mm');

            const closeTime = moment(shipperCloseTime, 'HH:mm');

            return receiveTime.isSameOrBefore(closeTime);
          }),
        shipperCloseTime: Yup.string()
          .required('Shipper Closer Time is required'),
        shippingSchedulingType: Yup.string()
          .required('Scheduling is required'),
      }).test('email-or-phone', 'Either email or phone number is required for requester', function (value) {
        const { shippingContactEmail, shippingContactPhoneNumber } = value;
        if (!shippingContactEmail && !shippingContactPhoneNumber) {
          return this.createError({ path: 'shippingContactEmail', message: 'Either email or phone number is required' });
        }
        return true;
      })

    case 3:
      return Yup.object().shape({
        receivingCompanyName: Yup.string()
          .required('Company Name is required'),
        receivingStreetAddress: Yup.string()
          .required('Street Address is required'),
        receivingCity: Yup.string()
          .required('City is required'),
        receivingState: Yup.string()
          .required('State is required'),
        receivingZipCode: Yup.string()
          .required('Zip Code is required'),
        receivingDockDoor: Yup.number()
          .typeError('Dock Door must be numeric'),
        receivingDockHeightFlag: Yup.string()
          .required('Dock Height Required is required'),
        receivingContactName: Yup.string().required('Contact Name is required').matches(/^[A-Za-z\s]+$/, 'Contact Name must be alphabetical'),
        receivingContactEmail: Yup.string().email('Invalid email').test('domain', 'Email must end with .com', validateEmailDomain),
        receivingContactPhoneNumber: Yup.string()
          .matches(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, 'Phone number must be in a valid format')
          .test(
            'is-valid-us-phone',
            'Phone number must be a valid US phone number',
            (value) => {
              if (!value) return true; // Allow empty for the conditional check
              const cleaned = ('' + value).replace(/\D/g, '');
              return cleaned.length === 10; // Validates if the phone number has exactly 10 digits
            }
          )
          .nullable(), // Allow null for the conditional check
        additionalReceivingContactsList: Yup.array().of(
          Yup.object().shape({
            alternateReceivingContactName: Yup.string()
              .matches(/^[A-Za-z\s]+$/, 'Contact name must be alphabetical'),
            alternateReceivingContactEmail: Yup.string()
              .email('Invalid email address')
              .test('domain', 'Email must end with .com', validateEmailDomain),
            alternateReceivingContactPhoneNumber: Yup.string()
              .matches(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, 'Phone number must be in a valid format')
              .test(
                'is-valid-us-phone',
                'Phone number must be a valid US phone number',
                (value) => {
                  if (!value) return true; // Allow empty for the conditional check
                  const cleaned = ('' + value).replace(/\D/g, '');
                  return cleaned.length === 10; // Validates if the phone number has exactly 10 digits
                }
              )
              .nullable(), // Allow null for the conditional check
          }).test(
            'name-required-when-email-or-phone',
            'Contact name is required when email or phone number is provided',
            function (value) {
              const { alternateReceivingContactName, alternateReceivingContactEmail, alternateReceivingContactPhoneNumber } = value;
              if ((alternateReceivingContactEmail || alternateReceivingContactPhoneNumber) && !alternateReceivingContactName) {
                return this.createError({
                  path: `${this.path}.alternateReceivingContactName`,
                  message: 'Contact name is required when email or phone number is provided',
                });
              }
              return true;
            }
          ).test(
            'email-or-phone-required',
            'Either email or phone number is required when contact name is provided',
            function (value) {
              const { alternateReceivingContactName, alternateReceivingContactEmail, alternateReceivingContactPhoneNumber } = value;
              if (alternateReceivingContactName && !alternateReceivingContactEmail && !alternateReceivingContactPhoneNumber) {
                return this.createError({
                  path: `${this.path}.alternateReceivingContactEmail`,
                  message: 'Either email or phone number is required when contact name is provided',
                });
              }
              return true;
            }
          )
        ),
        receivingRequiredDate: Yup.string()
          .required('Product Ready Date is required')
          .test('is-not-backdated', 'Product Ready Date cannot be in the past', function (value) {
            return new Date(value).getTime() >= new Date().setHours(0, 0, 0, 0);
          })
          .test('receiving-after-shipping', 'Required date should be greater than shipping ready date', function (value) {
            const { receivingRequiredTime, receivingCloseTime, shippingProductReadyDate, shippingProductReadyTime, shipperCloseTime } = this.parent;
            if (!value || !receivingRequiredTime || !receivingCloseTime || !shippingProductReadyDate || !shippingProductReadyTime || !shipperCloseTime) {
              return true
            }
            const ship = moment(`${shippingProductReadyDate} ${shippingProductReadyTime}`, 'YYYY-MM-DD HH:mm');

            const ready = moment(`${value} ${receivingRequiredTime}`, 'YYYY-MM-DD HH:mm');

            return ready.isAfter(ship);

          }),
        receivingRequiredTime: Yup.string()
          .required('Product Ready Time is required')
          .test('before-close-time', 'Required Time should be before Receiving Location Close Time', function (value) {
            const { receivingCloseTime } = this.parent;

            if (!value || !receivingCloseTime) return true;

            const receiveTime = moment(value, 'HH:mm');

            const closeTime = moment(receivingCloseTime, 'HH:mm');

            return receiveTime.isSameOrBefore(closeTime);
          }),
        receivingCloseTime: Yup.string()
          .required('Shipper Closer Time is required'),
        receivingSchedulingType: Yup.string()
          .required('Scheduling is required'),
      }).test('email-or-phone', 'Either email or phone number is required for requester', function (value) {
        const { receivingContactEmail, receivingContactPhoneNumber } = value;
        if (!receivingContactEmail && !receivingContactPhoneNumber) {
          return this.createError({ path: 'receivingContactEmail', message: 'Either email or phone number is required' });
        }
        return true;
      });

    case 4:
      return Yup.object().shape({
        commodity: Yup.string().required('Commodity is required'),
        commodityOtherValue: Yup.string().when('commodity', {
          is: (value: string) => value === 'Others',
          then: (schema: Yup.StringSchema) => schema.required('Commodity is required when Others is selected'),
        }),
        quantity: Yup.number()
          .typeError('Quantity must be a number')
          .required('Quantity is required')
          .positive('Quantity must be a positive number'),
        containerType: Yup.string()
          .required('Container Type is required')
          .when('hazmat', {
            is: (value: string) => value === 'yes',
            then: (schema) => schema.notOneOf(['Parcel'], 'Container Type cannot be Parcel when Hazmat is Yes'),
          }).when('isLoadParcel', {
            is: (value: string) => value === 'yes',
            then: (schema: Yup.StringSchema) => schema.oneOf(['Piece'], 'Container type has to be box or piece if Load Parcel is Yes'),
            otherwise: (schema: Yup.StringSchema) => schema,
          }),
        productDimension: Yup.array()
          .of(Yup.string().nullable())
          .test('dimension-required', 'All dimensions (Length, Height, Width) are required', function (value) {
            if (!Array.isArray(value) || value.length !== 3) {
              return false;
            }
            return value.every(val => val !== undefined && val !== null && val.trim() !== '');
          })
          .test('is-numeric', 'All dimensions (Length, Height, Width) must be numbers', function (value) {
            if (!Array.isArray(value) || value.length !== 3) {
              return false;
            }
            return value.every(val => val === undefined || val === null || val.trim() === '' || !isNaN(Number(val)));
          })
          .test('dimension-sum', 'The sum of Length, Height, and Width must be 165 or below when Load Parcel is Yes', function (value) {
            const { isLoadParcel } = this.parent;
            if (isLoadParcel === 'yes') {
              const [length = 0, height = 0, width = 0] = value || [];
              return Number(length) + Number(height) + Number(width) <= 165;
            }
            return true;
          }),
        totalWeightLbs: Yup.number()
          .typeError('Total Weight must be a number')
          .required('Total Weight is required')
          .positive('Total Weight must be a positive number')
          .max(45000, 'Total Weight must be less than or equal to 45,000 lbs')
          .when('reeferRequired', {
            is: (value: any) => value === 'yes',
            then: (schema: Yup.NumberSchema) => schema.max(44000, 'Total Weight must be less than or equal to 44,000 lbs when Reefer is Yes'),
          })
          .when('expedite', {
            is: (value: any) => value === 'yes',
            then: (schema: Yup.NumberSchema) => schema.max(20000, 'Total Weight must be less than 20,000 lbs when Expedite is Yes'),
          })
          .when('isLoadParcel', {
            is: (value: any) => value === 'yes',
            then: (schema: Yup.NumberSchema) => schema.max(150, 'Total weight must be 150 lbs or below when isLoadParcel is yes'),
          }),
        loadValue: Yup.number().typeError('Load Value must be a number'),
        productAdditionalComments: Yup.string(),
        reeferRequired: Yup.string()
          .required('Reefer is required')
          .when('expedite', {
            is: (value: string) => value === 'yes',
            then: (schema: Yup.StringSchema) => schema.notOneOf(['yes'], 'Reefer load cannot be selected when Expedite is Yes'),
          })
          .when('fullTruckLoad', {
            is: 'no',
            then: (schema: Yup.StringSchema) => schema.notOneOf(['yes'], 'Reefer load cannot be selected when Full Truck Load is No'),
          })
          .test('isLoadParcel-reefer', 'Reefer cannot be required when isLoadParcel is yes', function (value) {
            const { isLoadParcel } = this.parent;
            return !(isLoadParcel === 'yes' && value === 'yes');
          }),
        temperatureRequirement: Yup.array()
          .of(Yup.string().nullable())
          .test('dimension-required', 'Both Temperature Min and Temperature Max are required', function (value) {
            const { reeferRequired } = this.parent;
            if (reeferRequired === 'yes') {
              if (!Array.isArray(value) || value.length !== 2) {
                return false;
              }
              const [temperatureMin, temperatureMax] = value;
              return temperatureMin !== undefined && temperatureMax !== undefined;
            }
            return true;
          })
          .test('is-numeric', 'Both Temperature Min and Temperature Max must be numbers', function (value) {
            const { reeferRequired } = this.parent;
            if (reeferRequired === 'yes') {
              if (!Array.isArray(value) || value.length !== 2) {
                return false;
              }
              return value.every(val => val !== undefined && val !== null && !isNaN(Number(val)));
            }
            return true;
          })

          .test('temperature-range', 'Temperature Min must be less than or equal to Temperature Max', function (value) {
            const { reeferRequired } = this.parent;
            if (reeferRequired === 'yes') {
              if (!Array.isArray(value) || value.length !== 2) {
                return true; // Allow for undefined case
              }
              const [temperatureMin, temperatureMax] = value;
              return temperatureMin === undefined || temperatureMax === undefined || Number(temperatureMin) <= Number(temperatureMax);
            }
            return true;
          }),
        hazmatFlag: Yup.string().required('Hazmat is required')
          .test('isLoadParcel-hazmat', 'Hazmat cannot be required when isLoadParcel is yes', function (value) {
            const { isLoadParcel } = this.parent;
            return !(isLoadParcel === 'yes' && value === 'yes');
          }),
        hazmatUnNumber: Yup.string().when('hazmatFlag', {
          is: 'yes',
          then: (schema: Yup.StringSchema) => schema.matches(/^UN\d{4}$/, 'Hazmat UN Number must start with "UN" followed by 4 digits with no space').required('Hazmat UN Number is required'),
          otherwise: (schema: Yup.StringSchema) => schema.nullable(),
        }),
        hazmatClass: Yup.string().when('hazmatFlag', {
          is: 'yes',
          then: (schema: Yup.StringSchema) => schema.required('Hazmat Class is required'),
          otherwise: (schema: Yup.StringSchema) => schema.nullable(),
        }),
        packagingGroup: Yup.string().when('hazmatFlag', {
          is: 'yes',
          then: (schema: Yup.StringSchema) => schema.required('Packaging Group is required'),
          otherwise: (schema: Yup.StringSchema) => schema.nullable(),
        }),

        handlingComments: Yup.string().when('hazmatFlag', {
          is: 'yes',
          then: (schema: Yup.StringSchema) => schema.required('Handling Comments are required'),
          otherwise: (schema: Yup.StringSchema) => schema.nullable(),
        }),

        expediteServiceFlag: Yup.string().required('Expedite Transit Service required'),
        fullTruckLoadFlag: Yup.string().required('Full Truck Load is required'),
        isLoadParcel: Yup.string().when('fullTruckLoadFlag', {
          is: (fullTruckLoadFlag: string) => fullTruckLoadFlag === 'no',
          then: (schema: Yup.StringSchema) => schema.required('Load Parcel required when Full Truck Load is No'),
          otherwise: (schema: Yup.StringSchema) => schema.nullable(),
        }),

        teamDeliveryFlag: Yup.string().required('Team Delivery Service is required'),
        specialHandlingRequired: Yup.string().required('Special Handling is required'),
        teamDeliveryApproverEmail: Yup.string()
          .email('Invalid email')
          .test('domain', 'Email must end with .com', validateEmailDomain)
          .when('teamDeliveryFlag', {
            is: (value: any) => value === 'yes',
            then: (schema: Yup.StringSchema) => schema.required('Team Delivery Approver Email is required when Team Delivery is Yes'),
            otherwise: (schema: Yup.StringSchema) => schema.nullable(),
          }),

        expediteApproverEmail: Yup.string()
          .email('Invalid email')
          .test('domain', 'Email must end with .com', validateEmailDomain)
          .when('expediteServiceFlag', {
            is: (value: any) => value === 'yes',
            then: (schema: Yup.StringSchema) => schema.required('Expedite Approver Email is required when Expedite Service is Yes'),
            otherwise: (schema: Yup.StringSchema) => schema.nullable(),
          }),
        dedicatedTransportFlag: Yup.string().when('isLoadParcel', {
          is: (value: any) => value === 'no',
          then: (schema: Yup.StringSchema) => schema.required('Dedicated Transport is required when Load Parcel is No'),
          otherwise: (schema: Yup.StringSchema) => schema.nullable(),
        }),
        freightClass: Yup.string().when('isLoadParcel', {
          is: (value: any) => value === 'no',
          then: (schema: Yup.StringSchema) => schema.required('Freight Class is required when Load Parcel is No'),
          otherwise: (schema: Yup.StringSchema) => schema.nullable(),
        }),
        dedicatedApproverEmail: Yup.string()
          .email('Invalid email')
          .test('domain', 'Email must end with .com', validateEmailDomain)
          .when('dedicatedTransportFlag', {
            is: (value: any) => value === 'yes',

            then: (schema: Yup.StringSchema) => schema.required('Dedicated Transport Approver Email is required when Dedicated Transport is Yes'),
            otherwise: (schema: Yup.StringSchema) => schema.nullable(),
          }),

        specialHandlingTypeList: Yup.array().of(Yup.string()).when('specialHandlingRequired', {
          is: (value: any) => value === 'yes',
          then: (schema) => schema.required('Special Handling Type is required').min(1, 'At least one Special Handling Type must be selected'),
          otherwise: (schema) => schema.notRequired(),
        }),

      });

    // Add other validation schemas for other steps
    default:
      return Yup.object().shape({});
  }
};

// validationSchemas.ts
export const validatePurchaseOrder = (
  newPO: string,
  primaryPO: string,
  additionalPOs: string[]
): string => {
  if (additionalPOs.includes(newPO) || newPO === primaryPO) {
    return 'Duplicate purchase order is not allowed';
  } else if (additionalPOs.length + 1 >= 20) {
    return 'Total purchase orders should not exceed 20';
  } else if (additionalPOs.length + 1 + 1 > 20) { // Check total count including primary
    return 'Total purchase orders including primary should not exceed 20';
  }
  return '';
};


export const getValidationSchema2 = (step: number) => {


}